import { FoodsIcon, FoodsIconButton, HeaderOptions, NumberWithUnit, ScoreCardHeader, WhiteBox } from "@foodpilot/foods";
import { Button, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ValidatedState, ValidatedTag } from "../../ValidatedTag";
import { Link } from "react-router";
import { NoScore } from "./NoScore";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { useApplicationParameters } from "src/api/client-api/appParams";
import { MainScore, NumericalScoreValue } from "src/api/client-api/olapScores/olapScoresSchema";

type SummaryBlockScoreOverviewProps = {
    title: {
        label: string;
        tag?: ValidatedState;
    };
    score: {
        id: number;
        value: NumericalScoreValue;
        unit: string;
    };
    selectedScore: MainScore | null;
    questionnaireTitle: string;
    link: string;
    questionnaireLink: string | undefined;
    doesPostExist?: boolean;
};
export const SummaryBlockScoreOverview = (props: SummaryBlockScoreOverviewProps) => {
    const { title, score, link, questionnaireTitle, questionnaireLink, doesPostExist = false } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const { isFeatureActivated } = useApplicationParameters();

    const areScoreDetailsActivated =
        isFeatureActivated("FS-company-score-details-distribution") ||
        isFeatureActivated("FS-company-score-details-comparison");
    const _scoreValue = score.value;

    // I do that for the regulatory here, but it should really be done at the BASE of the app.
    const scoreValue =
        _scoreValue === null || _scoreValue === undefined || typeof _scoreValue !== "object" ?
            _scoreValue
            // any because type says it cant be object, but it can
        :   Object.values(_scoreValue as any).reduce<number>((acc, value: any) => {
                const shouldBeCounted = value?.required === true;
                if (shouldBeCounted) {
                    acc += 1;
                }
                return acc;
            }, 0);

    return (
        <WhiteBox sx={{ height: "100%" }}>
            <Stack
                padding={theme.spacing(2)}
                gap={theme.spacing(2)}
                justifyContent={"space-between"}
                height={"100%"}
                borderRadius={"8px"}
            >
                <Stack flexDirection={"row"} justifyContent={"space-between"} alignItems={"start"}>
                    <TestVariationSelector currentScoreId={score.id} />
                    {questionnaireLink !== undefined && (
                        <Link to={questionnaireLink}>
                            <FoodsIconButton
                                size={"xs"}
                                icon={"diagnostic"}
                                withBackground
                                tooltip={{
                                    text: t(
                                        "scores.tooltip.scores_resulting_from_questionnaire",
                                        "Scores resulting from your {{title}} questionnaire.",
                                        { title: questionnaireTitle },
                                    ),
                                }}
                            />
                        </Link>
                    )}
                </Stack>
                <Stack flexDirection="row">
                    <ValidatedTag state={title.tag} />
                </Stack>
                <Stack flexDirection="row">
                    <NumberWithUnit value={scoreValue} unit={score.unit} size={"big"} />
                </Stack>
                {doesPostExist == false ?
                    <NoScore ssqTitle={questionnaireTitle} />
                : areScoreDetailsActivated ?
                    <GoToDetailButton link={link} score={scoreValue} />
                :   null}
            </Stack>
        </WhiteBox>
    );
};

type GoToDetailButtonProps = {
    link: string;
    score: NumericalScoreValue;
};
const GoToDetailButton = (props: GoToDetailButtonProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { link, score } = props;

    if (score === null) {
        return (
            <Button
                variant="ternary"
                endIcon={<FoodsIcon size={2} icon="arrowRight" />}
                disabled
                sx={{ width: "100%", textAlign: "justify", color: theme.custom.grey[2800] }}
            >
                <Typography variant="body-bold" sx={{ width: "100%" }}>
                    {t("scores.see_score_detail", "See detail")}
                </Typography>
            </Button>
        );
    }

    return (
        <Link to={link}>
            <Button
                variant="ternary"
                endIcon={<FoodsIcon size={2} icon="arrowRight" />}
                sx={{ width: "100%", textAlign: "justify", color: theme.custom.grey[2800] }}
            >
                <Typography variant="body-bold" sx={{ width: "100%" }}>
                    {t("scores.see_score_detail", "See detail")}
                </Typography>
            </Button>
        </Link>
    );
};

type HeaderWithExtra = HeaderOptions & {
    meta: {
        scoreId: number;
        variationId: number | null;
    };
};
type TestVariationSelectorProps = {
    currentScoreId: number;
};
const TestVariationSelector = (props: TestVariationSelectorProps) => {
    const { currentScoreId } = props;
    const { selectedVariations, allScoresWithVariations, selectVariation } = useEntityScores();

    const opts: HeaderWithExtra[] = allScoresWithVariations.flatMap((scoreVariation) => {
        if (scoreVariation.scoreId !== currentScoreId) {
            return [];
        }
        const meta = {
            scoreId: scoreVariation.scoreId,
            variationId: scoreVariation.variationId,
        };
        const opt: HeaderWithExtra = {
            id: scoreVariation.rowId,
            label: scoreVariation.label,
            group: scoreVariation.group,
            onClick: (options, section) => {
                selectVariation(scoreVariation.scoreId, scoreVariation.variationId);
            },
            meta: { ...meta },
        };
        return [{ ...opt }];
    });

    if (opts.length === 0) {
        // No Score ? Not possible
        return null;
    }

    const selectedOption =
        opts.find((opt) => {
            const foundVariation = selectedVariations.state.variations?.[currentScoreId];
            const isScoreFound = opt.meta.scoreId === currentScoreId;
            if (!foundVariation) {
                return isScoreFound;
            }
            const isVariationFound = opt.meta.variationId == foundVariation;
            return isScoreFound && isVariationFound;
        }) ?? opts[0];

    if (opts.length === 1) {
        return (
            <Stack flexDirection="column" sx={{ minWidth: 0 }}>
                <Typography
                    variant="h4"
                    alignSelf="left"
                    noWrap
                    title={selectedOption.group}
                    sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                >
                    {selectedOption.group}
                </Typography>
                {selectedOption.label !== null && (
                    <Typography
                        variant="body"
                        alignSelf="left"
                        noWrap
                        title={selectedOption.label}
                        sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                        {selectedOption.label}
                    </Typography>
                )}
            </Stack>
        );
    }

    return (
        <ScoreCardHeader
            selectedItemId={selectedOption.id}
            options={opts}
            variant="primary"
            orientation="vertical"
            extraStyle={{
                noExtraSpacing: true,
            }}
        />
    );
};
